import { FormikErrors } from 'formik';
import ImageResizer from 'react-image-file-resizer';
import {
    GenericObject,
    IMAGE_COMPRESSION,
    MAX_IMAGE_RESOLUTION,
    acceptedUploadFileTypesArray,
    applications,
    compressUploadFileTypesArray,
    constValues,
    currentYear,
    translations,
} from './consts';

export const fiveCalendarYearsFromNow = new Date().setFullYear(new Date().getFullYear() - 5, 0, 1);

export const calendarYearsFromNow = (numberOfYears: number, before: boolean) => {
    let endDate;
    if (before) {
        endDate = new Date().setFullYear(new Date().getFullYear() - numberOfYears, 0, 1);
    } else {
        endDate = new Date().setFullYear(new Date().getFullYear() + numberOfYears, 0, 1);
    }
    return endDate;
};

export const getApplicationId = () => {
    const url = window.location.href;
    const params = new URL(url).searchParams;
    const value = params.get('idAplikacija');
    if (value) {
        return parseInt(value);
    }
    throw new TypeError('Missing application id');
};

export const getUrlParameter = (name: string) => {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');

    const regex = new RegExp(`[?&]${name}=([^&#]*)`);
    const results = regex.exec(window.location.search);

    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

export const parseJwt = (token: string) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
        atob(base64)
            .split('')
            .map(function (c) {
                return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
            })
            .join(''),
    );

    return JSON.parse(jsonPayload);
};

export const getIsPortal = (appId: number) => {
    return applications.portal.ID === appId;
};

export const getIsWebsite = (appId: number) => {
    return applications.website.ID === appId;
};

export const getIsCallCenter = (appId: number) => {
    return applications.callCenter.ID === appId || applications.webis.ID === appId;
};

export const getIsSalesForce = (appId: number) => {
    return applications.callCenter.ID === appId;
};

export const prepareCountriesList = (countries: { id: number; naziv: string }[] = []) => {
    return countries.map((country) => {
        return {
            id: country.id,
            text: country.naziv,
            value: country.id,
        };
    });
};

export const prepareCitizenshipsList = (citizenships: { sifra: number; naziv: string }[] = []) => {
    return citizenships.map((citizenship) => {
        return {
            id: citizenship.sifra,
            text: citizenship.naziv,
            value: citizenship.sifra,
        };
    });
};

export const prepareCitiesList = (cities: { id: number; naziv: string; sifra: string }[]) => {
    return cities.map((city) => {
        return {
            id: city.id,
            value: `${city.id}/${city.naziv}`,
            label: city.naziv,
            code: city.sifra,
        };
    });
};

export const checkPhoneNumber = (e: string) => {
    const isPhoneNum = /^\+[\/()\d-]{10,15}$/;
    return isPhoneNum.test(e);
};

export const checkIfSerbianPhoneNumber = (e: string) => {
    const isPhoneNum = /^\+3816\d+/;
    return isPhoneNum.test(e);
};

/*
 * Check validity of Serbian phone number is it mobile phone number if start with +381 then must start with +3816
 */
export const checkIfSerbianMobPhoneNumber = (e: string) => {
    const isPhoneNum = /^\+381\d+/;
    if (isPhoneNum.test(e)) {
        return checkIfSerbianPhoneNumber(e);
    }
    return true;
};

export const checkPolicyNumber = (e: string) => {
    const isNum = /^\d{8,9}$/;
    return isNum.test(e);
};

export const checkEmail = (e: string) => {
    const isEmail = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
    return isEmail.test(e);
};

export const bytesToMb = (bytes: number, roundTo: number) => {
    const converted = convertBytesToMb(bytes);
    return roundTo ? converted.toFixed(roundTo) : converted;
};

export const convertBytesToMb = (bytes: number) => {
    return bytes / 1024 / 1024;
};

export const prepareHours = () => {
    const hours = Array.from(Array(24).keys())
        .map((hour) => (hour < 10 ? '0' + hour : hour))
        .map(String);

    return hours.map((hour) => {
        return {
            id: `hour-${hour}`,
            text: hour,
            value: hour,
        };
    });
};

export const hasValueAndIsSalesForce = (value: string) => {
    if (getIsSalesForce(getApplicationId()) && value && value !== '') {
        return true;
    }
};

/**
 * Check validity of entered Unique citizenship number (JMBG in Serbian)
 *
 * @remarks
 * More detail can be found at:
 * https://sr.wikipedia.org/wiki/%D0%88%D0%B5%D0%B4%D0%B8%D0%BD%D1%81%D1%82%D0%B2%D0%B5%D0%BD%D0%B8_%D0%BC%D0%B0%D1%82%D0%B8%D1%87%D0%BD%D0%B8_%D0%B1%D1%80%D0%BE%D1%98_%D0%B3%D1%80%D0%B0%D1%92%D0%B0%D0%BD%D0%B8%D0%BD%D0%B0
 *
 *  @param ucn - Unique citizenship number to be checked in string format
 *  @returns True if Unique citizenship number is valid acording to Serbian(exYU) laws
 */
export const checkUCN = (ucn: string): boolean => {
    if (validUCNBirthDate(ucn)) {
        // check if birth date is valid and ucn is 13 digit numeric
        const region: number = parseInt(ucn.substring(7, 9), 10);
        if ((region >= 10 && region <= 59) || (region >= 70 && region <= 99)) {
            // domestic citizens have control number validation
            let sum: number =
                2 * (parseInt(ucn.charAt(11), 10) + parseInt(ucn.charAt(5), 10)) +
                3 * (parseInt(ucn.charAt(10), 10) + parseInt(ucn.charAt(4), 10)) +
                4 * (parseInt(ucn.charAt(9), 10) + parseInt(ucn.charAt(3), 10)) +
                5 * (parseInt(ucn.charAt(8), 10) + parseInt(ucn.charAt(2), 10)) +
                6 * (parseInt(ucn.charAt(7), 10) + parseInt(ucn.charAt(1), 10)) +
                7 * (parseInt(ucn.charAt(6), 10) + parseInt(ucn.charAt(0), 10));
            sum = 11 - (sum % 11);
            return parseInt(ucn.charAt(12), 10) === (sum > 10 ? 0 : sum);
        } else return true;
    } else return false;
};

/**
 * Validate if birth date is valid for unique citizenship number
 *
 * @param ucn - Unique citizenship number to be checked in string format
 */
const validUCNBirthDate = (ucn: string) => {
    const isNum = /^([0-9]{13})$/;
    if (isNum.test(ucn)) {
        const day: number = parseInt(ucn.substring(0, 2), 10);
        const month: number = parseInt(ucn.substring(2, 4), 10);
        let year: number = parseInt(ucn.substring(4, 7), 10);
        year = year >= 900 ? year + 1000 : year + 2000;
        let daysInMonth = 31;
        if (month === 2) {
            daysInMonth = (year % 4 == 0 && year % 100 != 0) || year % 400 == 0 ? 29 : 28;
        } else if (month === 4 || month === 6 || month === 9 || month === 11) {
            daysInMonth = 30;
        }
        return day >= 1 && day <= daysInMonth && month >= 1 && month <= 12 && year >= 1900 && year <= currentYear;
    }
    return false;
};

/**
 * Return number of year for given date
 *
 * @param date - birth date
 */
const getAgeFromDate = (date: string): number => {
    const today = new Date();
    const birthDate = new Date(date);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    return age;
};

/**
 * Check if birth date is for adult person
 *
 * @param date - birth date
 */
export const checkIfAdult = (date: string): boolean => {
    if (getAgeFromDate(date) >= 18) {
        return true;
    } else {
        return false;
    }
};

/**
 * Check if birth date is same as birth date from UCN
 *
 * @param date - birth date
 * @param ucn - Unique citizenship number to be checked in string format
 */
export const checkUCNWithBirthDate = (date: string, ucn: string): boolean => {
    const birthDate = new Date(date);
    const day = birthDate.getDate().toString().padStart(2, '0');
    const month = (birthDate.getMonth() + 1).toString().padStart(2, '0');
    const year = birthDate.getFullYear().toString().slice(-3);
    const formattedDate = `${day}${month}${year}`;

    return formattedDate === ucn.slice(0, 7);
};

/**
 * Get birth date from UCN if UCN birth date is valid
 *
 * @param ucn - Unique citizenship number in string format
 */
export const getBirthDateFromUCN = (ucn: string): Date | null => {
    if (validUCNBirthDate(ucn)) {
        const day: number = parseInt(ucn.substring(0, 2), 10);
        const month: number = parseInt(ucn.substring(2, 4), 10);
        let year: number = parseInt(ucn.substring(4, 7), 10);
        year = year >= 900 ? year + 1000 : year + 2000;
        return new Date(`${year}-${month}-${day}`);
    } else return null;
};

/**
 * Check validity of entered Legal entity ID (MB in Serbian)
 *
 * @remarks
 * More detail can be found at:
 * https://arhiva.elitesecurity.org/t488289-Kontrola-maticnog-broja-privrednog-subjekta
 *
 *  @param legalEntityId - LegalEntityId to be checked in string format
 *  @returns True if Legal entity ID number is valid acording to Serbian laws
 */
export const checkLegalEntityId = (legalEntityId: string): boolean => {
    const isNum = /^([0-9]{8})$/;
    if (isNum.test(legalEntityId)) {
        let sum: number =
            2 * (parseInt(legalEntityId.charAt(6), 10) + parseInt(legalEntityId.charAt(0), 10)) +
            3 * parseInt(legalEntityId.charAt(5), 10) +
            4 * parseInt(legalEntityId.charAt(4), 10) +
            5 * parseInt(legalEntityId.charAt(3), 10) +
            6 * parseInt(legalEntityId.charAt(2), 10) +
            7 * parseInt(legalEntityId.charAt(1), 10);
        sum = 11 - (sum % 11);
        return parseInt(legalEntityId.charAt(7), 10) === (sum > 9 ? 0 : sum);
    }
    return false;
};

/**
 * Check validity of entered VAT (Pib in Serbian) number
 *
 * @remarks
 * More detail can be found at:
 * https://sr.wikipedia.org/wiki/Poreski_identifikacioni_broj
 * https://regos.hr/app/uploads/2018/07/kont_br_rac.pdf ISO 7064, MODUL (11,10).
 *
 *  @param vat - VAT number to be checked in string format
 *  @returns True if VAT number is valid acording to Serbian laws
 */
export const checkVat = (vat: string): boolean => {
    const isNum = /^100000[1-9][0-9]|^[1-9][0-9]{8}$/;
    if (isNum.test(vat)) {
        let sum = 10;
        for (let i = 0; i < 8; i++) {
            sum = (sum + parseInt(vat.charAt(i), 10)) % 10;
            sum = ((sum === 0 ? 10 : sum) * 2) % 11;
        }
        sum = (11 - sum) % 10;
        return parseInt(vat.charAt(8), 10) === sum;
    }
    return false;
};

/**
 * Check validity of entered Serbian bank account
 *
 * @remarks
 * More detail can be found at:
 * https://www.nbs.rs/export/sites/NBS_site/documents/propisi/propisi-ps/jedinstvena_struktura_tekucih_racuna.pdf
 *
 *  @param accountNo - Bank account to be checked in string format
 *  @returns True if account number number is valid acording to Serbian laws
 */
export const checkSerbianBankAccount = (accountNo: string): boolean => {
    accountNo = accountNo.replaceAll('-', ''); // remove dashes in number
    const isNum = /^([0-9]{6,18})$/; // format: 3 digits for bank code, up to 13 digits for account number, 2 digits for control code
    if (isNum.test(accountNo)) {
        const bank: string = accountNo.substring(0, 3);
        const account: string = accountNo.substring(3, accountNo.length - 2).padStart(13, '0'); // pad to 13 character
        const control: bigint = BigInt(accountNo.substring(accountNo.length - 2, accountNo.length));
        const num: bigint = BigInt(bank + account + '00'); // number 16 characters without control digits used for calculation

        const mod: bigint = BigInt(98) - (num % BigInt(97));
        if (control === mod) return true;
        else return false;
    }

    return false;
};

/**
 * Check validity of entered Serbian IBAN
 *
 * @remarks
 * More detail can be found at:
 * https://www.nbs.rs/export/sites/NBS_site/documents/propisi/propisi-ps/jedinstvena_struktura_tekucih_racuna.pdf
 *
 *  @param accountNo - Serbian IBAN to be checked in string format
 *  @returns True if account number number is valid acording to Serbian laws
 */
export const checkSerbianIBAN = (accountNo: string): boolean => {
    if (accountNo.startsWith('RS35')) return checkSerbianBankAccount(accountNo.slice(4));
    return false;
};

const toDate = (date: string) => {
    return new Date(date);
};

export const formatDate = (date: string) => {
    const formatDateValue =
        date !== ''
            ? `${toDate(date).getFullYear()}-${('0' + (toDate(date).getMonth() + 1)).slice(-2)}-${(
                  '0' + toDate(date).getDate()
              ).slice(-2)}`
            : date;
    return formatDateValue;
};

export const formatPhoneValue = (value: string) => {
    const phoneValue = value && value.length > 4 ? value : '';
    return phoneValue;
};

export const formatDateSrb = (date: string) => {
    const formatDateValue =
        date !== ''
            ? `${('0' + toDate(date).getDate()).slice(-2)}.${('0' + (toDate(date).getMonth() + 1)).slice(-2)}.${toDate(
                  date,
              ).getFullYear()}.`
            : date;
    return formatDateValue;
};

export const isCountrySerbia = (countryId: string | number): boolean => {
    return countryId === constValues.countrySerbiaId.toString(10) || countryId === constValues.countrySerbiaId;
};

export const removeEmptyProperties = (array: any) => {
    return array.filter((obj: any) => {
        for (const value in obj) {
            if (obj[value] === ' ') return false;
        }
        return true;
    });
};

export const scrollToFirstError = (errors: FormikErrors<GenericObject>) => {
    const errorKeys = Object.keys(errors);

    if (errorKeys.length > 0) {
        const firstElement = document.querySelector(`[name=${errorKeys[0]}]`) as HTMLElement;

        const getTopOffset = (el: HTMLElement): number => {
            const labelOffset = 150;
            return el?.getBoundingClientRect().top + window.scrollY - labelOffset;
        };

        window.scroll({
            top: getTopOffset(firstElement),
            left: 0,
            behavior: 'smooth',
        });
    }
};

/**
 * Check if value has maximum 2 decimal places
 *
 *  @param value - number
 *  @returns True if value has maximum 2 decimal places, else false
 */
export const doesValueHaveMaxTwoDecimalPlaces = (value: string) => {
    const arr = value.split('.');
    return (arr.length === 2 && arr[1].length <= 2) || arr.length < 2;
};

interface IProps {
    id: string;
    documents: object;
}

/**
 * Can be used for document preparation for upload
 * @param List of documents with originalModified attibute
 * @returns Prepared document for upload
 */
export const prepareDocumentUploadFormData = ({ id, documents }: IProps) => {
    const formData = new FormData();
    const files = Object.values(documents)
        .filter((f) => f !== '')
        .map((f) => {
            const fileForPrepare = f.value;
            return new File([fileForPrepare], `${f.originalModified}@#$${fileForPrepare.name}`, {
                type: fileForPrepare.type,
            });
        });

    const dmsDocumentTypes = Object.values(documents).reduce((acc: GenericObject, f: GenericObject) => {
        const fileForPrepare = f.value;
        return {
            ...acc,
            [`${f.originalModified}@#$${fileForPrepare.name}`]: f.documentId,
        };
    }, {});

    files?.forEach((file: File) => {
        formData.append('files', new File([file], file.name, { type: file.type }));
    });

    formData.append('dmsDocumentTypes', JSON.stringify(dmsDocumentTypes));
    formData.append('stetaTempId', id);
    return formData;
};

export const totalUploadSize = (uploadDocList: any) => {
    let size = 0;
    if (uploadDocList.length > 0) {
        size = uploadDocList
            .map((item: any) => item.value.size)
            .reduce((totalSize: number, currentSize: number) => totalSize + currentSize, 0);

        return convertBytesToMb(size);
    }
    return size;
};

export type AddFilesToDocumentListResult = {
    valid: boolean;
    errorMsgText: string | null;
    errorFileOverLimitSize: string | null;
    errorFileExceedsAllowedSize: string | null;
    uploadDocList: any | null;
};

export const addFilesToDocumentList = async (
    docList: any,
    maxFileSize: number,
    maxSize: number,
    field: string,
    event: any,
    documentId: number,
) => {
    const resizeFile = async (file: File, fileExtension: string) =>
        new Promise<File>((resolve) => {
            const compressFormat: string = fileExtension == 'png' ? 'png' : 'jpeg';
            ImageResizer.imageFileResizer(
                file,
                MAX_IMAGE_RESOLUTION,
                MAX_IMAGE_RESOLUTION,
                compressFormat,
                IMAGE_COMPRESSION,
                0,
                (result) => {
                    const newFile: File = new File([result as any], file.name, {
                        lastModified: file.lastModified,
                        type: file.type,
                    });
                    resolve(newFile);
                },
                'blob',
            );
        });

    const result: AddFilesToDocumentListResult = {
        valid: false,
        errorMsgText: null,
        errorFileOverLimitSize: null,
        errorFileExceedsAllowedSize: null,
        uploadDocList: null,
    };
    const listOfExistsFile: string[] = [];
    const listOfNotValidExtFile: string[] = [];
    const listOfLargeFile: string[] = [];
    const listOfOverLimitFile: string[] = [];

    for (let index = 0; index < event.target.files.length; index++) {
        let fileForUpload = event.target.files[index];
        const extFile = fileForUpload.name.substring(fileForUpload.name.lastIndexOf('.') + 1).toLowerCase();
        const originalSize = fileForUpload.size;
        const originalModified = fileForUpload.lastModified;

        if (compressUploadFileTypesArray.includes(extFile)) {
            const compressedFile = await resizeFile(fileForUpload, extFile);
            if (compressedFile.size < originalSize)
                // if compressed size is smaller use that file
                fileForUpload = compressedFile;
        }

        const sizeMB = convertBytesToMb(fileForUpload.size);
        const sizeWithCurrentFile = totalUploadSize(docList) + sizeMB;
        if (sizeMB <= maxFileSize && sizeWithCurrentFile <= maxSize) {
            let fileExists = false;
            let fileAlreadyUploaded = false;

            for (let i = 0; i < docList.length; i++) {
                if (docList[i].value && docList[i].value.name === fileForUpload.name) {
                    // A file with the same name already exists in the docList
                    fileExists = true;
                    listOfExistsFile.push(fileForUpload.name);
                    break;
                }
                if (
                    docList[i].value &&
                    docList[i].originalModified === originalModified &&
                    docList[i].originalSize === originalSize
                ) {
                    // A file with the same last modified timestamp already exists in the docList
                    fileAlreadyUploaded = true;
                    listOfExistsFile.push(fileForUpload.name);
                    break;
                }
            }

            const validExtFile = acceptedUploadFileTypesArray.includes(extFile);
            if (!validExtFile) {
                listOfNotValidExtFile.push(fileForUpload.name);
            }

            if (!fileExists && !fileAlreadyUploaded && validExtFile) {
                docList.push({
                    field,
                    value: fileForUpload,
                    documentId,
                    originalSize,
                    originalModified,
                });
                result.valid = true;
                result.uploadDocList = docList;
            }
        } else {
            if (sizeMB > maxFileSize) {
                listOfLargeFile.push(fileForUpload.name);
            } else if (sizeWithCurrentFile > maxSize) {
                listOfOverLimitFile.push(fileForUpload.name);
            }
        }
    }

    result.errorMsgText =
        listOfExistsFile.length > 0
            ? `${translations.documentation.FILE_ALREADY_UPLOADED} (${listOfExistsFile.join(', ')})`
            : listOfNotValidExtFile.length > 0
            ? `${translations.documentation.FORMAT_NOT_ALLOWED} (${listOfNotValidExtFile.join(', ')})`
            : null;
    result.errorFileExceedsAllowedSize =
        listOfLargeFile.length > 0
            ? `${translations.documentation.EXEEEDS_ALLOWED_SIZE} ${maxFileSize} ${
                  translations.documentation.DOCUMENTATION_MB
              } ${listOfLargeFile.join(', ')}`
            : null;
    result.errorFileOverLimitSize =
        listOfOverLimitFile.length > 0
            ? `${translations.documentation.TOTAL_UPLOAD_SIZE} ${maxSize} ${
                  translations.documentation.DOCUMENTATION_MB
              } ${translations.documentation.TOTAL_UPLOAD_SIZE_SUFFIX} ${listOfOverLimitFile.join(', ')}`
            : null;
    return result;
};
